import { NgClass } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ProductType } from 'src/app/services/form/form.resources';
import { FormService } from 'src/app/services/form/form.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

type BadgeTone = 'green' | 'blue' | 'navy';
type StepTone = 'blue' | 'indigo' | 'teal' | 'grey';

interface TutorialStep {
  titleKey: string;
  descKey: string;
  icon: string;
  tone: StepTone;
  faded?: boolean;
  lineDownDashed?: boolean;
  lineUpDashed?: boolean;
}

interface TutorialSlide {
  type: ProductType;
  titleKey: string;
  badgeKey: string;
  badgeTone: BadgeTone;
  descKey: string;
  cardIcon: string;
  cardClass: string;
  steps: TutorialStep[];
}

@Component({
  selector: 'app-case-form-tutorial',
  templateUrl: './case-form-tutorial.component.html',
  styleUrls: ['./case-form-tutorial.component.scss'],
  imports: [NgClass, TranslateModule],
})
export class CaseFormTutorialComponent implements OnInit {
  private formService = inject(FormService);
  private translate = inject(TranslateService);

  readonly currentLang = this.translate.currentLang || this.translate.defaultLang;

  readonly loading = signal(false);
  readonly slides: TutorialSlide[] = [
    {
      type: 'demand_letter',
      titleKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.TITLE',
      badgeKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.BADGE',
      badgeTone: 'green',
      descKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.DESC',
      cardIcon: 'assets/case-form-tutorial/lawyer-letter-card.svg',
      cardClass: 'lawyer-letter',
      steps: [
        {
          titleKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.STEP1_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.STEP1_DESC',
          icon: 'assets/case-form-tutorial/timeline-file-blue.svg',
          tone: 'blue',
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.STEP2_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.STEP2_DESC',
          icon: 'assets/case-form-tutorial/timeline-mail.svg',
          tone: 'blue',
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.STEP3_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.STEP3_DESC',
          icon: 'assets/case-form-tutorial/timeline-mail-checked.svg',
          tone: 'blue',
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.STEP4_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.STEP4_DESC',
          icon: 'assets/case-form-tutorial/timeline-circle-check-blue.svg',
          tone: 'blue',
          lineDownDashed: true,
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.STEP5_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.LAWYER_LETTER.STEP5_DESC',
          icon: 'assets/case-form-tutorial/timeline-lightning-grey.svg',
          tone: 'grey',
          faded: true,
          lineUpDashed: true,
        },
      ],
    },
    {
      type: 'hard_1',
      titleKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.TITLE',
      badgeKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.BADGE',
      badgeTone: 'blue',
      descKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.DESC',
      cardIcon: 'assets/case-form-tutorial/payment-order-card.svg',
      cardClass: 'payment-order',
      steps: [
        {
          titleKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.STEP1_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.STEP1_DESC',
          icon: 'assets/case-form-tutorial/timeline-file-indigo.svg',
          tone: 'indigo',
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.STEP2_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.STEP2_DESC',
          icon: 'assets/case-form-tutorial/timeline-upload-indigo.svg',
          tone: 'indigo',
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.STEP3_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.STEP3_DESC',
          icon: 'assets/case-form-tutorial/timeline-doc-fold-indigo.svg',
          tone: 'indigo',
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.STEP4_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.STEP4_DESC',
          icon: 'assets/case-form-tutorial/timeline-circle-check-indigo.svg',
          tone: 'indigo',
          lineDownDashed: true,
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.STEP5_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.PAYMENT_ORDER.STEP5_DESC',
          icon: 'assets/case-form-tutorial/timeline-map-pin-grey.svg',
          tone: 'grey',
          faded: true,
          lineUpDashed: true,
        },
      ],
    },
    {
      type: 'coface',
      titleKey: 'CASE_FORM.TUTORIAL.COFACE.TITLE',
      badgeKey: 'CASE_FORM.TUTORIAL.COFACE.BADGE',
      badgeTone: 'navy',
      descKey: 'CASE_FORM.TUTORIAL.COFACE.DESC',
      cardIcon: 'assets/shared/global-images/partner-logos/coface.svg',
      cardClass: 'coface',
      steps: [
        {
          titleKey: 'CASE_FORM.TUTORIAL.COFACE.STEP1_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.COFACE.STEP1_DESC',
          icon: 'assets/case-form-tutorial/timeline-file-teal.svg',
          tone: 'teal',
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.COFACE.STEP2_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.COFACE.STEP2_DESC',
          icon: 'assets/case-form-tutorial/timeline-upload-teal.svg',
          tone: 'teal',
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.COFACE.STEP3_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.COFACE.STEP3_DESC',
          icon: 'assets/case-form-tutorial/timeline-mail-teal.svg',
          tone: 'teal',
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.COFACE.STEP4_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.COFACE.STEP4_DESC',
          icon: 'assets/case-form-tutorial/timeline-circle-check-teal.svg',
          tone: 'teal',
          lineDownDashed: true,
        },
        {
          titleKey: 'CASE_FORM.TUTORIAL.COFACE.STEP5_TITLE',
          descKey: 'CASE_FORM.TUTORIAL.COFACE.STEP5_DESC',
          icon: 'assets/case-form-tutorial/timeline-lightning-grey.svg',
          tone: 'grey',
          faded: true,
          lineUpDashed: true,
        },
      ],
    },
  ];
  readonly activeSlideIndex = signal(0);
  readonly selectedProduct = signal<ProductType | null>(null);
  readonly partnerSzamlazzhu = signal(false);

  readonly prevSlideTitleKey = computed(() => {
    const count = this.slides.length;
    const index = (this.activeSlideIndex() - 1 + count) % count;
    return this.slides[index].titleKey;
  });

  readonly nextSlideTitleKey = computed(() => {
    const count = this.slides.length;
    const index = (this.activeSlideIndex() + 1) % count;
    return this.slides[index].titleKey;
  });

  ngOnInit(): void {
    const c = this.formService.case;
    this.partnerSzamlazzhu.set(c?.partner_id === 'szamlazzhu');
    const t = c?.type;
    if (t === 'demand_letter' || t === 'hard_1' || t === 'coface') {
      this.selectedProduct.set(t);
      const index = this.slides.findIndex((slide) => slide.type === t);
      this.activeSlideIndex.set(Math.max(0, index));
    }
  }

  prevSlide(event?: Event): void {
    event?.stopPropagation();
    this.goToSlide(
      (this.activeSlideIndex() - 1 + this.slides.length) % this.slides.length,
      event,
    );
  }

  nextSlide(event?: Event): void {
    event?.stopPropagation();
    this.goToSlide(
      (this.activeSlideIndex() + 1) % this.slides.length,
      event,
    );
  }

  goToSlide(index: number, event?: Event): void {
    event?.stopPropagation();
    if (index < 0 || index >= this.slides.length) {
      return;
    }

    this.activeSlideIndex.set(index);
  }

  async onContinue(): Promise<void> {
    await this.formService.closeTutorial();
  }

  async changeProduct(product_type: ProductType): Promise<void> {
    const slideIndex = this.slides.findIndex((slide) => slide.type === product_type);
    if (slideIndex >= 0) {
      this.goToSlide(slideIndex);
    }

    if (this.loading() || this.selectedProduct() === product_type) {
      return;
    }

    this.selectedProduct.set(product_type);
    if (
      this.formService.case?.type !== product_type &&
      this.formService.case?.partner_id !== 'szamlazzhu'
    ) {
      this.loading.set(true);
      try {
        await this.formService.changeProduct(product_type);
        await this.formService.setFormData('debtor');
        this.formService.setFormGroup();
      } catch (error) {
        console.error('Error while changing product', error);
      } finally {
        this.loading.set(false);
      }
    }
  }
}
